// Service tier definitions with features and limits
export const SERVICE_TIERS = {
  free: {
    name: "Free",
    model: "gpt-4o",
    availableModels: [
      // All models available for free tier, with 3 post limit
      // OpenAI models
      "gpt-4o", 
      "gpt-4o-mini", 
      "gpt-4.5-preview",
      // Anthropic models
      "claude-3-haiku-20240307",
      "claude-3-5-sonnet-20240620",
      "claude-3-opus-latest",
      "claude-4-sonnet-latest",
      "claude-4-opus-latest",
    ],
    monthlyLimit: 3,
    features: {
      maxWordCount: 2000,
      customStyles: true,
      marketerMode: false,
      prioritySupport: false,
    },
  },
  pro: {
    name: "Pro",
    model: "gpt-4o",
    availableModels: [
      // OpenAI models
      "gpt-4o", 
      "gpt-4o-mini", 
      "gpt-4.5-preview",
      "o3-mini", 
      "o1", 
      "o1-mini",
      // Anthropic models
      "claude-3-haiku-20240307",
      "claude-3-5-sonnet-20240620",
      "claude-3-opus-latest",
      "claude-3-7-sonnet-latest",
      "claude-4-sonnet-latest",
      "claude-4-opus-latest",
    ],
    monthlyLimit: 15,
    features: {
      maxWordCount: 4000,
      customStyles: true,
      marketerMode: true,
      prioritySupport: false,
    },
    pricing: {
      monthly: {
        founder: "$9.95/month",
        regular: "$24.95/month"
      },
      yearly: {
        founder: "$79.95/year",
        regular: "$249.50/year"
      }
    }
  },
  enterprise: {
    name: "Enterprise",
    model: "gpt-4o",
    availableModels: [
      // OpenAI models
      "gpt-4o", 
      "gpt-4o-mini", 
      "o3-mini", 
      "o1", 
      "o1-mini",
      // Anthropic models
      "claude-3-haiku-20240307",
      "claude-3-5-sonnet-20240620",
      "claude-3-opus-latest",
      "claude-3-7-sonnet-latest",
      "claude-4-sonnet-latest",
      "claude-4-opus-latest",
    ],
    monthlyLimit: 100,
    features: {
      maxWordCount: 4000,
      customStyles: true,
      marketerMode: true,
      prioritySupport: true,
    },
    pricing: {
      monthly: {
        founder: "$49.95/month",
        regular: "$110/month"
      },
      yearly: {
        founder: "$379/year",
        regular: "$1,100/year"
      }
    }
  },
  enterpriseVIP: {
    name: "Enterprise VIP",
    model: "gpt-4o",
    availableModels: [
      // OpenAI models
      "gpt-4o", 
      "gpt-4o-mini", 
      "gpt-4.5-preview",
      "o3-mini", 
      "o1", 
      "o1-mini",
      // Anthropic models
      "claude-3-haiku-20240307",
      "claude-3-5-sonnet-20240620",
      "claude-3-opus-latest",
      "claude-3-7-sonnet-latest",
      "claude-4-sonnet-latest",
      "claude-4-opus-latest",
    ],
    monthlyLimit: 1000,
    features: {
      maxWordCount: 8000,
      customStyles: true,
      marketerMode: true,
      prioritySupport: true,
      unlimitedModels: true,
    },
  },
} as const;

export type ServiceTier = keyof typeof SERVICE_TIERS;

// Helper function to get tier features
export function getTierFeatures(tier: ServiceTier) {
  return SERVICE_TIERS[tier];
}

// Helper function to check if user has access to a feature
export function hasFeatureAccess(tier: ServiceTier, feature: keyof typeof SERVICE_TIERS.enterprise.features): boolean {
  return SERVICE_TIERS[tier].features[feature];
}

// Helper function to get the OpenAI model for a tier
export function getModelForTier(tier: ServiceTier): string {
  return SERVICE_TIERS[tier].model;
}

// Helper function to get monthly limit for a tier
export function getMonthlyLimit(tier: ServiceTier): number {
  return SERVICE_TIERS[tier].monthlyLimit;
}

// Helper function to check if the model is an Anthropic model
export function isAnthropicModel(model: string): boolean {
  return model.startsWith('claude-');
}